import React, { useEffect, useState, useMemo } from "react";
import * as SearchApi from "@src/api/SearchApi";
import Tile from "@src/components/containers/Tile";
import ProductImage from "@src/components/widgets/ProductImage";
import { Link } from "react-router-dom";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import { SimpleTable } from "@swordsweeper/ssi-ui-component-library";
import DistributorLogo from "@src/components/common/DistributorLogo";
import classnames from "classnames";
import TileThemes from "@src/components/containers/Tile/TileThemes";
import { formatDateShort } from "@src/utils/formatDate";
import ClickedOutsideContainer from "@src/components/containers/ClickedOutsideContainer";
import orderBy from "lodash/orderBy";
import find from "lodash/find";
import { useCurrentUserStore } from "@src/state/currentUserState";
import {
  DistributorApiHealthStatus,
  DistributorApiHealthStatuses,
} from "@src/constants/DistributorApiHealthStatuses";
import { DISTRIBUTOR_NAMES } from "@src/constants/Distributors";
import {
  DistributorConnectionStatus,
  DistributorConnectionStatuses,
} from "@src/components/widgets/AccountDistributorSettings/DistributorConnectionConstants";

interface SearchResultsPageProps {
  product: SearchApi.SearchProductResult;
}

export default function SearchResultsPageProductTile({
  product,
}: SearchResultsPageProps): React.JSX.Element {
  const {
    distributorConnections,
    loadingDistributorConnections,
    distributorApiHealthStatuses,
    fetchDistributorConnections,
    isDistributorConnected,
    getDistributorConnection,
  } = useCurrentUserStore();
  const [showLastOrderedDetailsId, setShowLastOrderedDetailsId] = useState<
    string | null
  >(null);

  // Calculate the distributor status reactively, updating when distributorConnections or distributorApiHealthStatuses change
  const distributorStatusMapping = useMemo(() => {
    const mapping = {} as Record<
      string,
      DistributorApiHealthStatus | DistributorConnectionStatus
    >;
    product.distributorItems.forEach(({ distributor }) => {
      const connection = getDistributorConnection(distributor);
      const connectionStatus = connection?.status;

      // Only show API health status for CONNECTED distributors
      // NOT_CONNECTED distributors should be filtered out entirely, even if they have API health issues
      if (connectionStatus === DistributorConnectionStatuses.Connected) {
        const distributorApiHealthStatus = find(distributorApiHealthStatuses, {
          distributor,
        })?.status;
        if (
          distributorApiHealthStatus ===
            DistributorApiHealthStatuses.ServiceOutage ||
          distributorApiHealthStatus ===
            DistributorApiHealthStatuses.ScheduledMaintenance
        ) {
          mapping[distributor] = distributorApiHealthStatus;
          return;
        }
      }

      // Map connection status
      if (connectionStatus === DistributorConnectionStatuses.Connected) {
        mapping[distributor] = DistributorConnectionStatuses.Connected;
      } else {
        mapping[distributor] = DistributorConnectionStatuses.NotConnected;
      }
    });
    return mapping;
  }, [
    product.distributorItems,
    distributorConnections,
    distributorApiHealthStatuses,
    getDistributorConnection,
  ]);

  useEffect(() => {
    // Fetch distributor connections if not already loaded
    if (!distributorConnections && !loadingDistributorConnections) {
      fetchDistributorConnections();
    }
  }, [
    distributorConnections,
    loadingDistributorConnections,
    fetchDistributorConnections,
  ]);

  const mostRecentlyOrderedDistributorItem = product.distributorItems.reduce(
    (
      mostRecent: SearchApi.SearchDistributorItem,
      current: SearchApi.SearchDistributorItem,
    ) => {
      if (!mostRecent.lastOrderedDetails?.id) {
        return current;
      }
      if (!current.lastOrderedDetails?.id) {
        return mostRecent;
      }
      return new Date(current.lastOrderedDetails.placedAtTimestamp) >
        new Date(mostRecent.lastOrderedDetails.placedAtTimestamp)
        ? current
        : mostRecent;
    },
    { lastOrderedDetails: null } as SearchApi.SearchDistributorItem,
  );

  const getLowestPriceAndAvailability = (
    pricesAndAvailabilities: SearchApi.SearchDistributorItem["pricesAndAvailabilities"],
  ) => {
    if (!pricesAndAvailabilities) {
      return null;
    }

    let lowest = null;

    for (const item of pricesAndAvailabilities) {
      if (!lowest || item.price < lowest.price) {
        lowest = item;
      }
    }

    return lowest;
  };

  const orderedDistributorItems = orderBy(
    product.distributorItems,
    ["isPreferred"],
    ["desc"],
  );

  // Filter out NOT_CONNECTED distributors from the table
  // Only filter if distributorConnections have been loaded (not null)
  const filteredDistributorItems = useMemo(() => {
    if (distributorConnections === null) {
      // If connections haven't loaded yet, show all distributors (backward compatibility)
      return orderedDistributorItems;
    }
    return orderedDistributorItems.filter((item) => {
      // Check connection status directly - only show CONNECTED distributors
      // NOT_CONNECTED distributors are filtered out entirely, even if they have API health issues
      const connection = getDistributorConnection(item.distributor);
      if (
        !connection ||
        connection.status === DistributorConnectionStatuses.NotConnected
      ) {
        return false; // Filter out NOT_CONNECTED
      }
      // Show CONNECTED distributors (they may have API health issues, but that's OK)
      return true;
    });
  }, [
    orderedDistributorItems,
    getDistributorConnection,
    distributorConnections,
  ]);

  // Use the pre-built pdpUrl (includes group_attributes as query params) when available.
  // Falls back to the bare product URL for results indexed before this change.
  const tileLink = product.pdpUrl ?? `/product/${product.id}`;
  // Non-standalone products require variant selection on the PDP before pricing is meaningful.
  const showPricing =
    product.isStandalone !== false && filteredDistributorItems.length <= 4;

  return (
    <Tile className="padding-xl flex-row spaced-content-xl">
      <Link
        to={tileLink}
        data-testid={`search-result-product-image-link-${product.id}`}
      >
        <ProductImage
          sizePx={180}
          imageSrc={product.imageUrl}
          imageAlt={product.itemName}
        />
      </Link>

      <div className="flex-1">
        <div className="flex-row">
          <div className="flex-1">
            <Link
              className="text-default display-block width-fit-content"
              to={tileLink}
              data-testid={`search-result-product-name-link-${product.id}`}
            >
              <div className="text-lg font-weight-bold width-fit-content">
                {product.itemName}
              </div>
            </Link>
          </div>
        </div>

        {!showPricing && (
          <p
            className="text-sm text-secondary margin-top-md"
            data-testid={`search-result-product-select-options-${product.id}`}
          >
            Select options on product page to see pricing
          </p>
        )}

        {showPricing && (
          <SimpleTable
            className="table-last-column-align-right"
            hideTopBorder
            columns={[
              {
                header: "Vendor",
                key: "notificationType",
                render: ({
                  distributor,
                  isPreferred,
                }: SearchApi.SearchDistributorItem) => (
                  <div
                    className="flex-row flex-center flex-start spaced-content-lg"
                    data-testid={`search-result-product-distributor-${product.id}-${distributor}`}
                  >
                    <DistributorLogo distributorSlug={distributor} />
                    {isPreferred && (
                      <i className="fa-solid fa-star text-xs text-primary" />
                    )}
                  </div>
                ),
                width: 200,
              },
              {
                header: "Availability",
                key: "availability",
                render: ({
                  distributor,
                  pricesAndAvailabilities,
                }: SearchApi.SearchDistributorItem) => {
                  if (
                    distributorStatusMapping[distributor] !==
                    DistributorConnectionStatuses.Connected
                  ) {
                    return null;
                  }

                  const lowestPriceAndAvailability =
                    getLowestPriceAndAvailability(pricesAndAvailabilities);
                  return (
                    <span
                      data-testid={`search-result-product-availability-${product.id}-${distributor}`}
                    >
                      {lowestPriceAndAvailability
                        ? lowestPriceAndAvailability.availabilityStatus
                        : "Out of Stock"}
                    </span>
                  );
                },
              },
              {
                header: "Past Orders",
                key: "pastOrderCount",
                render: ({
                  distributor,
                  pastOrderCount,
                  lastOrderedDetails,
                }: SearchApi.SearchDistributorItem) => {
                  if (
                    distributorStatusMapping[distributor] !==
                    DistributorConnectionStatuses.Connected
                  ) {
                    return null;
                  }

                  const isMostRecentlyOrdered =
                    mostRecentlyOrderedDistributorItem.lastOrderedDetails?.id &&
                    mostRecentlyOrderedDistributorItem.lastOrderedDetails.id ===
                      lastOrderedDetails?.id;
                  const isShowingLastOrdered =
                    showLastOrderedDetailsId &&
                    showLastOrderedDetailsId === lastOrderedDetails?.id;

                  return (
                    <div
                      className="text-sm flex-row flex-center width-100 position-relative"
                      data-testid={`search-result-product-past-orders-${product.id}-${distributor}`}
                    >
                      <ClickedOutsideContainer
                        onClickedOutside={() =>
                          setShowLastOrderedDetailsId(null)
                        }
                      >
                        <div
                          className={classnames({
                            "circled circled-primary circled-font-size-md clickable":
                              !!isMostRecentlyOrdered,
                          })}
                          onClick={() =>
                            setShowLastOrderedDetailsId(
                              (isShowingLastOrdered
                                ? null
                                : lastOrderedDetails?.id) || null,
                            )
                          }
                          data-testid={`search-result-product-past-orders-count-${product.id}-${distributor}`}
                        >
                          {pastOrderCount || 0}
                        </div>

                        {isShowingLastOrdered && (
                          <div
                            className="position-absolute top-0 left-0 margin-top-lg margin-left-md"
                            data-testid={`search-result-product-last-ordered-details-${lastOrderedDetails.id}-${distributor}`}
                          >
                            <Tile className="text-xs" theme={TileThemes.Dark}>
                              <div className="flex-row space-between nowrap spaced-content-xxl">
                                <span className="text-secondary">
                                  Last Ordered
                                </span>
                                <span
                                  className="text-default"
                                  data-testid={`search-result-product-last-ordered-date-${lastOrderedDetails.id}-${distributor}`}
                                >
                                  {formatDateShort(
                                    lastOrderedDetails?.placedAtTimestamp,
                                  )}
                                </span>
                              </div>

                              <div className="flex-row space-between nowrap spaced-content-xxl">
                                <span className="text-secondary">
                                  Price Per Unit
                                </span>
                                <span className="text-default">
                                  <PriceDisplay
                                    amount={lastOrderedDetails?.pricePerUnit}
                                    data-testid={`search-result-product-last-ordered-price-${lastOrderedDetails.id}-${distributor}`}
                                  />
                                </span>
                              </div>

                              <div className="flex-row space-between nowrap spaced-content-xxl">
                                <span className="text-secondary">
                                  Units Ordered
                                </span>
                                <span
                                  className="text-default"
                                  data-testid={`search-result-product-last-ordered-units-${lastOrderedDetails.id}-${distributor}`}
                                >
                                  {lastOrderedDetails?.unitsOrdered}
                                </span>
                              </div>
                            </Tile>
                          </div>
                        )}
                      </ClickedOutsideContainer>
                    </div>
                  );
                },
                width: 90,
              },
              {
                header: "Price",
                key: "price",
                render: ({
                  distributor,
                  pricesAndAvailabilities,
                }: SearchApi.SearchDistributorItem) => {
                  // Only show API health messages for CONNECTED
                  // NOT_CONNECTED distributors are filtered out entirely
                  const connection = getDistributorConnection(distributor);
                  const isConnected =
                    connection?.status ===
                    DistributorConnectionStatuses.Connected;

                  if (isConnected) {
                    if (
                      distributorStatusMapping[distributor] ===
                      DistributorApiHealthStatuses.ScheduledMaintenance
                    ) {
                      return (
                        <div
                          className="text-secondary text-italic nowrap"
                          data-testid={`search-result-product-price-scheduled-maintenance-message-${product.id}-${distributor}`}
                        >
                          {DISTRIBUTOR_NAMES[distributor]} pricing is currently
                          unavailable due to scheduled maintenance
                        </div>
                      );
                    }

                    if (
                      distributorStatusMapping[distributor] ===
                      DistributorApiHealthStatuses.ServiceOutage
                    ) {
                      return (
                        <div
                          className="text-secondary text-italic nowrap"
                          data-testid={`search-result-product-price-service-outage-message-${product.id}-${distributor}`}
                        >
                          {DISTRIBUTOR_NAMES[distributor]} pricing is currently
                          unavailable due to a service outage
                        </div>
                      );
                    }
                  }

                  const lowestPriceAndAvailability =
                    getLowestPriceAndAvailability(pricesAndAvailabilities);
                  const minQuantity =
                    lowestPriceAndAvailability?.minQuantity ?? 1;
                  return (
                    <div className="flex-column justify-flex-end">
                      {!!lowestPriceAndAvailability ? (
                        <div className="text-lg font-weight-bold">
                          <span>
                            {pricesAndAvailabilities.length > 1 && (
                              <span className="text-xs">
                                starting at &nbsp;
                              </span>
                            )}
                            <PriceDisplay
                              amount={lowestPriceAndAvailability.price}
                              data-testid={`search-result-product-price-display-${product.id}-${distributor}`}
                            />
                          </span>
                          {minQuantity > 1 && (
                            <span
                              className="text-xs text-secondary font-weight-normal margin-left-xs"
                              data-testid={`search-result-product-price-min-quantity-${product.id}-${distributor}`}
                            >
                              ({minQuantity}+ qty)
                            </span>
                          )}
                        </div>
                      ) : (
                        <div
                          className="text-secondary text-italic"
                          data-testid={`search-result-product-price-unavailable-message-${product.id}-${distributor}`}
                        >
                          Pricing Unavailable
                        </div>
                      )}
                      {/* TODO: Other prices will be derived from buying groups and should render as new table rows */}
                    </div>
                  );
                },
                width: 240,
              },
            ]}
            data={filteredDistributorItems}
          />
        )}
      </div>
    </Tile>
  );
}
